import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;
const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export type ActivityCategory = "auth" | "page_view" | "action" | "read" | "api";
export type ActivityRole = "all" | "client" | "admin";

export interface ActivityTarget {
    type: string;
    id?: string;
    name?: string;
}

export interface ActivityEntry {
    _id: string;
    userId: string;
    userEmail: string;
    isAdmin?: boolean;
    impersonated?: boolean;
    apiKeyId?: string;
    apiKeyName?: string;
    category: ActivityCategory;
    action: string;
    label: string;
    method?: string;
    route?: string;
    url?: string;
    statusCode?: number;
    durationMs?: number;
    page?: string;
    targets?: ActivityTarget[];
    payload?: any;
    ip?: string;
    userAgent?: string;
    geo?: { country?: string; city?: string };
    createdAt: string;
}

export interface ActivityFeedFilters {
    role?: ActivityRole;
    userId?: string;
    apiKeyId?: string;
    category?: ActivityCategory[];
    action?: string;
    q?: string;
    from?: string;
    to?: string;
    before?: string;
    after?: string;
    hideApi?: boolean;
    limit?: number;
}

export interface ActivityUser {
    userId: string;
    email: string;
    name: string;
    company?: string;
    isAdmin: boolean;
    isSub: boolean;
    trialEndsAt?: string;
    signedUpAt?: string;
    lastAt: string;
    lastIp?: string;
    events: number;
    actions: number;
    pageViews: number;
    logins: number;
    activeDays: number;
}

export interface ActivityStats {
    days: number;
    totals: { events: number; activeUsers: number; actions: number; pageViews: number; logins: number; failedLogins: number };
    daily: { day: string; activeUsers: number; events: number; actions: number }[];
    topActions: { action: string; label: string; category: ActivityCategory; count: number; users: number }[];
    topPages: { page: string; count: number; users: number }[];
    topUsers: { userId: string; email: string; count: number }[];
}

export const getActivityFeed = async (f: ActivityFeedFilters) => {
    const params = new URLSearchParams();
    if (f.role && f.role !== "all") params.set("role", f.role);
    if (f.userId) params.set("userId", f.userId);
    if (f.apiKeyId) params.set("apiKeyId", f.apiKeyId);
    if (f.category?.length) params.set("category", f.category.join(","));
    if (f.action) params.set("action", f.action);
    if (f.q) params.set("q", f.q);
    if (f.from) params.set("from", f.from);
    if (f.to) params.set("to", f.to);
    if (f.before) params.set("before", f.before);
    if (f.after) params.set("after", f.after);
    if (f.hideApi) params.set("hideApi", "true");
    params.set("limit", String(f.limit ?? 50));
    const res = await axios.get(`/admin/activity/feed?${params}`, config());
    return res.data as { entries: ActivityEntry[]; hasMore: boolean; retentionDays: number };
};

export const getActivityUsers = async (days: number, role: ActivityRole = "all") => {
    const params = new URLSearchParams({ days: String(days) });
    if (role !== "all") params.set("role", role);
    const res = await axios.get(`/admin/activity/users?${params}`, config());
    return res.data.users as ActivityUser[];
};

export const getActivityStats = async (days: number, userId?: string, role: ActivityRole = "all") => {
    const params = new URLSearchParams({ days: String(days) });
    if (role !== "all") params.set("role", role);
    if (userId) params.set("userId", userId);
    const res = await axios.get(`/admin/activity/stats?${params}`, config());
    return res.data as ActivityStats;
};
